import { useState, useEffect, useRef } from 'react';
import type { PlatformState, PermissionState, ChannelDetectionItem } from '../hooks/useOnboardingState';
import apiService from '../../../services/api';
import { isTrustedOAuthMessage } from '../../../utils/oauthMessage';

interface Step1SocialChannelsProps {
  ecosystemUrl: string;
  urlScanning: boolean;
  urlScanned: boolean;
  platforms: PlatformState[];
  permissionsExpanded: boolean;
  permissions: PermissionState;
  isStep1Valid: boolean;
  channelDetection: ChannelDetectionItem[];
  urlErrors: Record<string, string>;
  urlDirty: Record<string, boolean>;
  onEcosystemUrlChange: (value: string) => void;
  onPlatformUrlChange: (id: string, value: string) => void;
  onPlatformUrlBlur: (id: string, value: string) => void;
  onConnectPlatform: (id: string, code?: string) => void;
  onDisconnectPlatform: (id: string) => void;
  onTogglePermissionsExpanded: () => void;
  onTogglePermission: (key: keyof PermissionState) => void;
}

export default function Step1SocialChannels({
  ecosystemUrl,
  urlScanning,
  urlScanned,
  platforms,
  permissionsExpanded,
  permissions,
  isStep1Valid,
  channelDetection,
  urlErrors,
  urlDirty,
  onEcosystemUrlChange,
  onPlatformUrlChange,
  onPlatformUrlBlur,
  onConnectPlatform,
  onDisconnectPlatform,
  onTogglePermissionsExpanded,
  onTogglePermission,
}: Step1SocialChannelsProps) {
  const [showPermissionPulse, setShowPermissionPulse] = useState(false);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!isTrustedOAuthMessage(event)) return;
      if (event.data?.type === 'SMM_OAUTH_CODE') {
        const ch = event.data.platform;
        const code = event.data.code;
        onConnectPlatform(ch, code);
        setConnectingId(null);
        popupRef.current = null;
      } else if (event.data?.type === 'SMM_OAUTH_ERROR') {
        setConnectingId(null);
        popupRef.current = null;
        alert(`Failed to connect ${event.data.platform}: ${event.data.error || 'Authentication failed'}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onConnectPlatform]);

  const handleOAuthConnect = async (id: string) => {
    const savedUserId = localStorage.getItem('user_id');
    if (!savedUserId) {
      alert('User ID not found. Please restart onboarding.');
      return;
    }

    try {
      setConnectingId(id);
      const res = await apiService.getSMMChannelOAuthUrl(id, savedUserId);
      const popup = window.open(
        res.authorization_url,
        `${id}_oauth`,
        'width=600,height=700,left=100,top=100'
      );
      if (!popup) {
        setConnectingId(null);
        alert('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.');
        return;
      }
      popupRef.current = popup;

      const poll = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(poll);
          setConnectingId((prev) => (prev === id ? null : prev));
        }
      }, 500);
    } catch (err: any) {
      setConnectingId(null);
      // apiService errors carry the backend's reason in .message
      alert(err?.message || `Failed to start ${id} OAuth`);
    }
  };

  const handleGrantPermissions = () => {
    onTogglePermissionsExpanded();
    setShowPermissionPulse(true);
    setTimeout(() => setShowPermissionPulse(false), 600);
  };

  const allPermissionsGranted = Object.values(permissions).every(Boolean);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center mb-2">
        {/* <h2 className="text-xl font-bold text-gray-900 mb-1">Social Channel Setup</h2> */}
        <p className="text-sm text-gray-500">Connect your digital ecosystem and grant data access permissions</p>
      </div>

      {/* URL Sniffer */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Platform Rows */}
        <div className="space-y-3">
          {platforms.map((platform) => (
            <div key={platform.id}>
              {/* Main Platform Row */}
              <div
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${platform.connected
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-gray-50 border-gray-100'
                  }`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${platform.connected
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-white text-gray-400 border border-gray-200'
                    }`}
                >
                  <i className={`${platform.icon} text-lg`}></i>
                </div>
                <div className="flex flex-col flex-shrink-0">
                  <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                  {platform.id === 'facebook' && (
                    <span className="text-[10px] text-gray-400">Instagram auto-links</span>
                  )}
                </div>

                {platform.connected ? (
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                      Connected
                    </span>
                    <span className="text-xs text-gray-400 truncate">{platform.url}</span>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      {/* Prefill badge */}
                      {(() => {
                        const PLATFORM_ID_TO_KEY: Record<string, string> = {
                          linkedin: 'linkedin', youtube: 'youtube',
                          facebook: 'meta_facebook', instagram: 'meta_instagram',
                        };
                        const key = PLATFORM_ID_TO_KEY[platform.id];
                        const detected = Array.isArray(channelDetection) ? channelDetection.find(c => c.platform === key) : undefined;
                        if (detected?.prefilled && platform.url && !urlDirty[platform.id]) {
                          return (
                            <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                              <i className="ri-magic-line text-[10px]"></i>Auto-filled
                            </span>
                          );
                        }
                        return null;
                      })()}
                      <input
                        type="text"
                        value={platform.url}
                        onChange={(e) => onPlatformUrlChange(platform.id, e.target.value)}
                        onBlur={(e) => onPlatformUrlBlur(platform.id, e.target.value)}
                        placeholder={platform.id === 'facebook' || platform.id === 'linkedin' ? `Paste ${platform.name} Page URL...` : 'Paste channel link here...'}
                        className={`flex-1 min-w-0 px-3 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 transition-colors ${
                          urlDirty[platform.id] && urlErrors[platform.id]
                            ? 'border-red-400 focus:ring-red-400 bg-red-50'
                            : 'border-gray-200 focus:ring-teal-500'
                        }`}
                      />
                      {(platform.id === 'facebook' || platform.id === 'linkedin') && import.meta.env.VITE_ENABLE_ANALYTICS !== 'true' ? (
                        <div className="flex items-center gap-2">
                          <div style={{ position: 'relative', opacity: 0.5, pointerEvents: 'none' }}>
                            <button
                              disabled={!platform.url.trim() || !!connectingId}
                              className="px-3 py-1.5 bg-teal-500 text-white text-xs font-medium rounded-md hover:bg-teal-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center"
                            >
                              {connectingId === platform.id ? (
                                <>
                                  <i className="ri-loader-4-line animate-spin mr-1"></i>
                                  Connecting
                                </>
                              ) : (
                                'Connect'
                              )}
                            </button>
                          </div>
                          <span style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            background: '#f3f4f6',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: '500'
                          }}>Coming Soon</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleOAuthConnect(platform.id)}
                          disabled={!platform.url.trim() || !!connectingId || !!(urlErrors[platform.id])}
                          className="px-3 py-1.5 bg-teal-500 text-white text-xs font-medium rounded-md hover:bg-teal-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center"
                        >
                          {connectingId === platform.id ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-1"></i>
                              Connecting
                            </>
                          ) : (
                            'Authenticate'
                          )}
                        </button>
                      )}
                    </div>
                    {/* Inline URL validation error */}
                    {urlDirty[platform.id] && urlErrors[platform.id] && (
                      <p className="text-[11px] text-red-500 flex items-center gap-1 ml-1">
                        <i className="ri-error-warning-line text-[11px]"></i>
                        {urlErrors[platform.id]}
                      </p>
                    )}
                  </div>
                )}

                {platform.connected && (
                  <button
                    onClick={() => onDisconnectPlatform(platform.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer w-8 h-8 flex items-center justify-center"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                )}
              </div>

              {/* Instagram Auto-Linked Info */}
              {platform.id === 'facebook' && platform.connected && platform.instagram_username && (
                <div className="ml-12 mt-2">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 border border-purple-200">
                    <div className="w-6 h-6 rounded flex items-center justify-center bg-purple-100">
                      <i className="ri-instagram-line text-purple-600 text-sm"></i>
                    </div>
                    <span className="text-xs font-medium text-gray-700">@{platform.instagram_username}</span>
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full ml-auto">
                      Instagram auto-linked
                    </span>
                  </div>
                </div>
              )}

              {/* Instagram check pending — neutral info */}
              {platform.id === 'facebook' && platform.connected && !platform.instagram_checked && !platform.instagram_username && (
                <div className="ml-12 mt-2">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                    <i className="ri-instagram-line text-gray-400 text-sm"></i>
                    <span className="text-xs text-gray-500">
                      Instagram will be auto-detected when you continue
                    </span>
                  </div>
                </div>
              )}

              {/* No Instagram Found Warning — only after backend check */}
              {platform.id === 'facebook' && platform.connected && platform.instagram_checked && !platform.instagram_username && (
                <div className="ml-12 mt-2">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-yellow-50 border border-yellow-200">
                    <i className="ri-information-line text-yellow-600 text-sm"></i>
                    <span className="text-xs text-yellow-700">
                      No Instagram Business account linked to this Facebook Page
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Grant Permissions CTA */}
        {platforms.some((p) => p.connected) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleGrantPermissions}
              className="px-5 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-all cursor-pointer flex items-center gap-2"
            >
              <i className="ri-shield-check-line"></i>
              Grant Sync Permissions
            </button>
          </div>
        )}
      </div>

      {/* Permissions Panel */}
      <div
        className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-500 ${permissionsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 border-transparent'
          }`}
      >
        <div className={`p-6 ${showPermissionPulse ? 'animate-pulse' : ''}`}>
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-lock-unlock-line text-teal-600"></i>
            <h3 className="text-sm font-semibold text-gray-800">Data Stream Permissions</h3>
            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-auto">
              Required for AI intelligence
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(
              [
                { key: 'reachAnalytics' as const, label: 'Reach & Engagement Analytics', icon: 'ri-bar-chart-box-line' },
                { key: 'audienceDemographics' as const, label: 'Audience Demographics', icon: 'ri-team-line' },
                { key: 'commentVisibility' as const, label: 'Comment Thread Visibility', icon: 'ri-message-3-line' },
                { key: 'watchTimeSubscriptions' as const, label: 'Video Watch-Time & Subscriptions', icon: 'ri-video-line' },
              ] as const
            ).map((perm) => (
              <label
                key={perm.key}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${permissions[perm.key]
                    ? 'bg-teal-50 border-teal-200'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${permissions[perm.key]
                      ? 'bg-teal-500 border-teal-500'
                      : 'border-gray-300'
                    }`}
                >
                  {permissions[perm.key] && <i className="ri-check-line text-white text-xs"></i>}
                </div>
                <input
                  type="checkbox"
                  checked={permissions[perm.key]}
                  onChange={() => onTogglePermission(perm.key)}
                  className="hidden"
                />
                <i className={`${perm.icon} text-gray-400 text-sm`}></i>
                <span className="text-sm text-gray-700">{perm.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Status hint */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-gray-400">
          {platforms.some((p) => p.connected)
            ? `${platforms.filter((p) => p.connected).length} platform(s) connected`
            : 'Connecting a platform is optional — you can do this later'}
        </span>
        {allPermissionsGranted && (
          <span className="text-xs text-emerald-600 flex items-center gap-1">
            <i className="ri-shield-check-line"></i>
            All permissions granted
          </span>
        )}
      </div>
    </div>
  );
}